import {
  type SnapshotBehaviourId,
  type SnapshotPayload,
  snapshotDefinition,
  snapshotTallySay,
} from "@healthos/shared";
import type { CSSProperties, ReactNode } from "react";
import { renderInlineMarkdown } from "./inline-markdown";
import { SnapshotRadar } from "./snapshot-radar";
import { Termed } from "./snapshot-term";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot-tokens";

export interface SnapshotHeroProps {
  payload: SnapshotPayload;
  focusedId: SnapshotBehaviourId | null;
  quietIds: ReadonlySet<SnapshotBehaviourId>;
  onFocus?: (id: SnapshotBehaviourId | null) => void;
  /** The week's focus: its heading and level card sit under the note. */
  focus?: { heading: string; card: ReactNode } | null;
  /**
   * The one reading the week is led by. The card keeps this shape whether or
   * not a usual range has formed, so an early week still reads as this card.
   */
  lead?: {
    label: string;
    /** Glossary key, so a headline term explains itself where it is read. */
    termKey?: string | null;
    display: string;
    plural?: boolean;
    /** The day is still being counted, so the headline takes no past tense. */
    inProgress?: boolean;
    /** Why this reading leads: it is outside the member's usual, or it is the one they chose. */
    reason?: "attention" | "focus";
    /** When it was measured, so the headline is never a figure without a date. */
    window?: string | null;
    /** What the reading means, when it has plain words. Leads instead of the metric. */
    meaning?: string | null;
    sub: string | null;
  } | null;
  /** Where the member is in their dose cycle, when they take one. */
  dose?: string | null;
  /** Shown under the note while the member's own ranges are still forming. */
  gathering?: ReactNode;
}

/**
 * One calm answer to "how am I": the reading the week turns on, what it was
 * measured against, the composer's read of it, and the behaviour being worked
 * on. The numbers live behind
 * the tabs, which is the whole reason the tabs exist.
 */
export function SnapshotHero({
  payload,
  focusedId,
  quietIds,
  onFocus,
  focus,
  lead,
  dose,
  gathering,
}: SnapshotHeroProps) {
  const focusedB = focusedId ? SNAPSHOT_BEHAVIOURS.find((b) => b.id === focusedId) : null;
  const focusedCorrelates: ReadonlyArray<SnapshotBehaviourId> =
    (focusedB && payload.rows[focusedB.id].correlates) || [];
  // A tally of the verdicts already on the rows, so the shape carries a plain
  // reading of itself. An ungraded behaviour is stated, never counted against.
  // Defaulted counts as unmeasured, the way it does everywhere else: a pillar
  // with no data scores 40 to 60 and would otherwise be tallied as a reading.
  const graded = SNAPSHOT_BEHAVIOURS.filter(
    (b) =>
      payload.scores[b.id].status !== "quiet" &&
      !quietIds.has(b.id) &&
      payload.scores[b.id].defaulted !== true,
  );
  const tally = snapshotTallySay(
    graded.filter((b) => payload.scores[b.id].status !== "watch").length,
    graded.length,
  );
  const ungraded = SNAPSHOT_BEHAVIOURS.filter(
    (b) => payload.scores[b.id].status === "quiet" || quietIds.has(b.id),
  )
    .map((b) => b.id)
    .join(" ");

  return (
    // The headline's emphasis wears the focused behaviour's hue (Sleep violet,
    // Food apricot); with no focus it stays plain ink, never an alarm colour.
    <section
      className="snap-hero"
      style={focusedB ? ({ "--hero-hue": focusedB.color } as CSSProperties) : undefined}
    >
      <div className="snap-hero-left">
        {lead ? (
          <>
            {/*
              The headline is the first sentence anyone reads, and on a bad
              week it opens on an abbreviation: "HRV was 20 ms." A reader who
              does not hold that term has nowhere to go, so a term that has a
              definition carries the way to it here too. The design leaves the
              marker off; a first sentence nobody can read is the worse fault.
            */}
            {/* Why this number and not another. The hero leads on the sharpest
                reading where there is one and on the focus behaviour where
                there is not, and a reading sitting inside its usual range
                arrived with nothing to say which. */}
            {lead.reason ? (
              <p className="snap-hero-why">
                {lead.reason === "attention" ? "The reading to look at" : "What you are working on"}
              </p>
            ) : null}
            {/* What the reading means, where it has plain words. The reading
                itself moves to the line below rather than being dropped: a
                member cannot act on "HRV was 14 ms", and a headline that
                leads with a metric leaves them to interpret their own body. */}
            <h2 className="snap-hero-h">
              {lead.meaning ? (
                <em>{lead.meaning}.</em>
              ) : (
                <>
                  <em>
                    <Termed label={lead.label} definition={snapshotDefinition(lead.termKey)} />
                  </em>{" "}
                  {/* A day still being counted has no past tense, and a
                      finished one says which. */}
                  {lead.inProgress ? (
                    <>so far today: {lead.display}.</>
                  ) : (
                    <>
                      {lead.plural ? "were" : "was"} {lead.display}
                      {lead.window ? ` ${lead.window.toLowerCase()}` : ""}.
                    </>
                  )}
                </>
              )}
            </h2>
            {/* The reading the headline was drawn from. Rendered here rather
                than folded into `sub` so the term keeps its underline and its
                definition on hover, which is how every other term on the page
                explains itself. */}
            {lead.meaning ? (
              <p className="snap-hero-note">
                <Termed label={lead.label} definition={snapshotDefinition(lead.termKey)} />{" "}
                {lead.plural ? "were" : "was"} {lead.display}
                {lead.window ? ` ${lead.window.toLowerCase()}` : ""}.{" "}
                {lead.sub ? renderInlineMarkdown(lead.sub) : null}
              </p>
            ) : lead.sub ? (
              <p className="snap-hero-note">{renderInlineMarkdown(lead.sub)}</p>
            ) : null}
          </>
        ) : (
          <h2 className="snap-hero-h">{renderInlineMarkdown(payload.headline)}</h2>
        )}
        {dose ? <p className="snap-hero-dose">{dose}</p> : null}
        <p className="snap-hero-read">{renderInlineMarkdown(payload.note)}</p>
        {gathering}
        {focus ? (
          <div className="snap-hero-focus">
            <div className="snap-section-head">
              <h3>{focus.heading}</h3>
            </div>
            {focus.card}
          </div>
        ) : null}
      </div>

      <div className="snap-hero-tree" data-ungraded={ungraded || undefined}>
        {/* Only the part the rows below do not already show. Each unscored
            behaviour says so on its own row ("Waiting for a logged meal"), and
            the note repeats it in prose. */}
        <p className="snap-tally">{renderInlineMarkdown(tally)}</p>
        <SnapshotRadar
          scores={payload.scores}
          focusedId={focusedId}
          relatedIds={
            new Set<SnapshotBehaviourId>(focusedB ? [focusedB.id, ...focusedCorrelates] : [])
          }
          quietIds={quietIds}
          onFocus={onFocus}
        />
      </div>
    </section>
  );
}
